import { times as _times } from 'lodash-es'
import { Factory, Model } from 'miragejs'
import { getQueryParamValue } from '@@/api/mock-server/utils.js'
import { getMockPeriodValues } from '@@/api/mock-server/reports/SummaryConsultation.js'
import { randMask } from '@ngneat/falso'
import { safeMathRandom } from '@/utils/number.js'

import type { ModelDefinition } from 'miragejs/-types'
import type { MirageServer, MockServer } from '@@/api/mock-server/server'
import type { ReportSummaryTimePeriod } from '@/components/reports/summary/config'
import type {
  SummaryDurationData,
  SummaryDurationDataBE,
} from '@/composables/api/ReportsSummary.js'

export const model = Model as ModelDefinition<SummaryDurationData>

const NR_OF_SERIES_ITEMS = 6

export const factory = Factory.extend<SummaryDurationDataBE>({
  id() {
    return randMask({ mask: '################' })
  },
  current_total() {
    return {
      period_label: '22 Sept-28 Sept',
      durations: {
        waiting_minutes: 18,
        consultation_minutes: 34,
        total_minutes: 52,
      },
    }
  },
  previous_total() {
    return {
      period_label: '15 Sept-21 Sept',
      durations: {
        waiting_minutes: 13,
        consultation_minutes: 45,
        total_minutes: 58,
      },
    }
  },
  totals_delta() {
    // Calculate delta: ((52 - 58) / 58) * 1000 = -103.45 (in tenths)
    // Round to -103 (which is -10.3%)
    return -103
  },
  current_series() {
    return _times(NR_OF_SERIES_ITEMS, (i) => ({
      index_label: String(i),
      real_label: ` ${i + 1}`,
      durations: {
        waiting_minutes: Math.floor(safeMathRandom() * 20) + 10,
        consultation_minutes: Math.floor(safeMathRandom() * 30) + 25,
        total_minutes: Math.floor(safeMathRandom() * 40) + 40,
      },
    }))
  },
  previous_series() {
    return _times(NR_OF_SERIES_ITEMS, (i) => ({
      index_label: String(i),
      real_label: ` ${i + 1}`,
      durations: {
        waiting_minutes: Math.floor(safeMathRandom() * 20) + 8,
        consultation_minutes: Math.floor(safeMathRandom() * 30) + 40,
        total_minutes: Math.floor(safeMathRandom() * 40) + 50,
      },
    }))
  },
})

export function routes(server: MockServer) {
  server.get(
    '/reports_v2/practice_performance_durations',
    function (this: MirageServer, schema, request) {
      const period_type = getQueryParamValue(
        request?.queryParams?.period_type,
      ) as ReportSummaryTimePeriod['type']
      const periodTypeLowerCase = period_type.toLowerCase() as Lowercase<
        typeof period_type
      >

      const summary = server.create('summaryDuration')
      const periodValues = getMockPeriodValues()

      const mockedDurationValues = {
        current: [
          { waiting: 15, consultation: 30, total: 45 },
          { waiting: 18, consultation: 32, total: 50 },
          { waiting: 20, consultation: 35, total: 55 },
          { waiting: 17, consultation: 33, total: 50 },
          { waiting: 19, consultation: 34, total: 53 },
          { waiting: 18, consultation: 34, total: 52 },
        ],
        previous: [
          { waiting: 12, consultation: 42, total: 54 },
          { waiting: 14, consultation: 44, total: 58 },
          { waiting: 13, consultation: 45, total: 58 },
          { waiting: 15, consultation: 43, total: 58 },
          { waiting: 13, consultation: 45, total: 58 },
          { waiting: 13, consultation: 45, total: 58 },
        ],
      }

      summary.attrs.current_total.period_label =
        periodValues[periodTypeLowerCase].current[NR_OF_SERIES_ITEMS - 1]
      summary.attrs.previous_total.period_label =
        periodValues[periodTypeLowerCase].previous[NR_OF_SERIES_ITEMS - 1]

      summary.attrs.current_series.forEach(
        (
          item: SummaryDurationDataBE['current_series'][number],
          index: number,
        ) => {
          item.real_label = periodValues[periodTypeLowerCase].current[index]
          const duration = mockedDurationValues.current[index]
          item.durations = {
            waiting_minutes: duration.waiting,
            consultation_minutes: duration.consultation,
            total_minutes: duration.total,
          }
        },
      )

      summary.attrs.previous_series.forEach(
        (
          item: SummaryDurationDataBE['previous_series'][number],
          index: number,
        ) => {
          item.real_label = periodValues[periodTypeLowerCase].previous[index]
          const duration = mockedDurationValues.previous[index]
          item.durations = {
            waiting_minutes: duration.waiting,
            consultation_minutes: duration.consultation,
            total_minutes: duration.total,
          }
        },
      )

      // Update totals from the last series item
      const lastCurrent = mockedDurationValues.current[NR_OF_SERIES_ITEMS - 1]
      const lastPrevious = mockedDurationValues.previous[NR_OF_SERIES_ITEMS - 1]

      summary.attrs.current_total.durations = {
        waiting_minutes: lastCurrent.waiting,
        consultation_minutes: lastCurrent.consultation,
        total_minutes: lastCurrent.total,
      }

      summary.attrs.previous_total.durations = {
        waiting_minutes: lastPrevious.waiting,
        consultation_minutes: lastPrevious.consultation,
        total_minutes: lastPrevious.total,
      }

      // Calculate totals_delta in tenths: ((current - previous) / previous) * 1000
      const currentTotal = summary.attrs.current_total.durations.total_minutes
      const previousTotal = summary.attrs.previous_total.durations.total_minutes

      summary.attrs.totals_delta =
        previousTotal === 0
          ? 0
          : Math.round(((currentTotal - previousTotal) / previousTotal) * 1000)

      return summary
    },
    { timing: 900 },
  )
}

export function seeds(server: MockServer) {
  server.create('summaryDuration')
}
